import { useMutation, useQuery } from '@apollo/client';
import { ColumnsType } from 'antd/es/table';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { Footer, TitleBar } from '../../../../../components';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import { OwnersInformation } from '../../components';

import {
  CREATE_CONSIDERATION,
  DELETE_CONSIDERATION,
  FIND_ALL_CONSIDERATION_BY_STRATEGY_CONSIDERATION,
  FIND_STRATEGY_CONSIDERATION_BY_STRATEGY,
  GET_SECTION_OWNER,
  UPDATE_CONSIDERATION,
  UPDATE_SECTION_OWNER,
} from '../../../queries';
import { STRATEGY_TYPES } from '../constants';
import { formFields } from './formFields';
import { tableSortingMethods } from '../../../../../utils/tableSortingMethods';
import { cleanUpObject } from '../../../../../utils';
import { baseColumns } from './ExportColumns';
import QueryErrorBoundary from '../../../../../components/error-handling';
import TitleUpdater from '../../../../../components/common/TitleUpdater';

type StrategyFacilityProps = {
  strategyType?: string;
};

const OtherConsiderations = ({
  strategyType = 'processArea',
}: StrategyFacilityProps) => {
  const { locationId, processAreaId, strategyId } = useParams();
  const [searchParams] = useSearchParams();

  const locationName = searchParams.get('locationName');
  const strategyLength = searchParams.get('strategyLength');

  let strategyName;

  if (strategyType === 'processArea') {
    strategyName = searchParams.get('name');
  }

  const subtitle =
    strategyType === 'processArea'
      ? `${locationName} // ${strategyName} // ${strategyLength} Month Strategy`
      : `${locationName} // ${strategyLength} Month Strategy`;

  const backPath =
    strategyType === 'processArea'
      ? `/process-areas-and-strategies/${locationId}/${processAreaId}`
      : `/strategy-summary/${locationId}/${strategyId}?locationName=${locationName}&strategyLength=${strategyLength}`;

  const OPERATION_NAMES = {
    SECTION_OWNER: 'GetSectionOwnerByStrategy',
    CONSIDERATIONS: 'FindAllConsiderationByStrategyConsideration',
    STRATEGY_CONSIDERATION: 'FindStrategyConsiderationByStrategy',
    DOCUMENTS: 'FindAllByStrategyConsideration',
  };

  const { data: strategyConsideration } = useQuery(
    FIND_STRATEGY_CONSIDERATION_BY_STRATEGY,
    {
      variables: {
        uuid: strategyId,
      },
      skip: !strategyId,
    },
  );

  const strategyConsiderationData =
    strategyConsideration?.findStrategyConsiderationByStrategy;

  const { data: strategyOwner, error: strategyOwnerError } = useQuery(
    GET_SECTION_OWNER,
    {
      variables: {
        uuid: strategyId,
        strategyType: STRATEGY_TYPES.OTHER_CONSIDERATION,
      },
      skip: !strategyId,
    },
  );

  const sectionOwner = strategyOwner?.findSectionOwnerByStrategy;

  const {
    data: otherConsiderations,
    loading: otherConsiderationsLoading,
    error: otherConsiderationsError,
  } = useQuery(FIND_ALL_CONSIDERATION_BY_STRATEGY_CONSIDERATION, {
    variables: {
      uuid: strategyConsiderationData?.uuid,
    },
    skip: !strategyConsiderationData?.uuid,
  });

  const otherConsiderationsData =
    otherConsiderations?.findAllConsiderationByStrategyConsideration;

  const [updateSectionOwner] = useMutation(UPDATE_SECTION_OWNER, {
    refetchQueries: [OPERATION_NAMES.SECTION_OWNER],
  });

  const [createConsideration] = useMutation(CREATE_CONSIDERATION, {
    refetchQueries: [OPERATION_NAMES.CONSIDERATIONS],
  });

  const [updateConsideration] = useMutation(UPDATE_CONSIDERATION, {
    refetchQueries: [OPERATION_NAMES.CONSIDERATIONS],
  });

  const [deleteConsideration] = useMutation(DELETE_CONSIDERATION, {
    refetchQueries: [OPERATION_NAMES.CONSIDERATIONS],
  });

  const inboundColumns: ColumnsType<any> = [
    {
      key: 'otherConsideration',
      title: 'Item',
      width: 150,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.otherConsideration, b.otherConsideration),
      render: ({ otherConsideration }) => (
        <span className="custom-table--column custom-table--column-primary">
          {otherConsideration}
        </span>
      ),
    },
    {
      key: 'description',
      title: 'Description',
      width: 250,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.description, b.description),
      render: ({ description }) =>
        description ? (
          <span className="custom-table--column">{description}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'impactOnMitigation',
      title: 'Impact on Mitigation Timeline (months)',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.impactOnMitigationTimeLine,
          b.impactOnMitigationTimeLine,
        ),
      render: ({ impactOnMitigationTimeLine }) =>
        impactOnMitigationTimeLine ? (
          <span className="custom-table--column">
            {impactOnMitigationTimeLine}
          </span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'internalContact',
      title: 'Internal Contact',
      width: 150,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.firstName, b.firstName),
      render: ({ firstName, lastName, email }) =>
        firstName || lastName || email ? (
          <div className="custom-table--column text-sm flex flex-col">
            <span>
              {firstName} {lastName}
            </span>
            <span>{email}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const modifyVariables = ({ values, type }: ModifyVariablesArgs) => {
    const {
      sectionOwnerfirstName,
      sectionOwnerlastName,
      email,
      impactOnMitigationTimeLine,
      ...restValues
    } = values;

    const fields = {
      ...restValues,
      firstName: sectionOwnerfirstName,
      lastName: sectionOwnerlastName,
      email: email,
      impactOnMitigationTimeLine: String(impactOnMitigationTimeLine),
    };

    const cleanedUpValues = cleanUpObject(fields);

    if (type === 'create') {
      return {
        ...cleanedUpValues,
        strategyConsideration: {
          uuid: strategyConsiderationData?.uuid,
        },
      };
    }
    return cleanedUpValues;
  };

  return (
    <>
      <TitleUpdater title={`Other Considerations`} />
      <TitleBar title="BCP Hub" subtitle={subtitle} />
      <section className="flex flex-col gap-4">
        <OwnersInformation
          title="Section Owner"
          data={sectionOwner}
          updateGQL={updateSectionOwner}
          error={strategyOwnerError}
        />
        <QueryErrorBoundary error={otherConsiderationsError}>
          <TableV3
            title="Other Considerations"
            columns={inboundColumns}
            data={otherConsiderationsData}
            loading={otherConsiderationsLoading}
            editable
            formFields={formFields}
            createGQL={createConsideration}
            updateGQL={updateConsideration}
            deleteGQL={deleteConsideration}
            modifyVariables={modifyVariables}
            exportExcelColumns={baseColumns}
          />
        </QueryErrorBoundary>
        <Footer backPath={backPath} />
      </section>
    </>
  );
};

export default OtherConsiderations;
